import React, { useState, useEffect, useRef } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import NavBar from "./components/NavBar";
import HomePage from "./components/HomePage";
import PhotographersPage from "./components/PhotographersPage/PhotographersPage";
import PhotographerDetail from "./components/PhotographerDetail/PhotographerDetail";
import TimelinePage from "./components/TimelinePage/TimelinePage";
import GenrePage from "./components/GenrePage/GenrePage";
import SubgenreDetailPage from "./components/SubgenreDetailPage/SubgenreDetailPage";
import DarkModeToggle from "./components/DarkModeToggle";
import LanguageSwitcher from "./components/LanguageSwitcher";
import Footer from "./components/Footer/Footer";
import TimelineIntro from "./components/TimelineIntro";
import "./App.css";
import ScrollToTop from "./ScrollToTop";
import About from "./components/About";
import NewsletterPrompt from "./components/NewsletterPrompt/NewsletterPrompt";

const getInitialLanguage = () => {
  if (typeof window === "undefined") return "EN";

  const saved = localStorage.getItem("language");
  if (saved === "CN" || saved === "EN") return saved;

  const currentLocale =
    (Array.isArray(navigator.languages) && navigator.languages[0]) ||
    navigator.language ||
    "";
  const usesChinese = String(currentLocale).toLowerCase().startsWith("zh");

  return usesChinese ? "CN" : "EN";
};

function AppContent() {
  const location = useLocation();
  const isHome = location.pathname === "/";
  const [isMenuHidden, setIsMenuHidden] = useState(false);
  const [language, setLanguage] = useState(getInitialLanguage); // 用户选择的目标语言
  const [displayLanguage, setDisplayLanguage] = useState(getInitialLanguage); // 当前页面显示语言
  const [languageSwitchPhase, setLanguageSwitchPhase] = useState(""); // "" | "out" | "in"
  const switchTimersRef = useRef({ outTimer: null, inTimer: null });

  const clearLanguageSwitchTimers = () => {
    if (switchTimersRef.current.outTimer) {
      clearTimeout(switchTimersRef.current.outTimer);
      switchTimersRef.current.outTimer = null;
    }
    if (switchTimersRef.current.inTimer) {
      clearTimeout(switchTimersRef.current.inTimer);
      switchTimersRef.current.inTimer = null;
    }
  };

  useEffect(() => {
    return () => clearLanguageSwitchTimers();
  }, []);

  const handleLanguageChange = (code) => {
    if (code !== "CN" && code !== "EN") return;
    if (code === language) return;

    setLanguage(code);
    localStorage.setItem("language", code);

    const reduceMotion =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    clearLanguageSwitchTimers();

    if (reduceMotion) {
      setDisplayLanguage(code);
      setLanguageSwitchPhase("");
      return;
    }

    // 先淡出旧文案，再切换语言，最后淡入新文案
    setLanguageSwitchPhase("out");
    switchTimersRef.current.outTimer = setTimeout(() => {
      setDisplayLanguage(code);
      setLanguageSwitchPhase("in");
      switchTimersRef.current.inTimer = setTimeout(() => {
        setLanguageSwitchPhase("");
      }, 180);
    }, 110);
  };

  return (
    <div
      className={`app-shell ${displayLanguage === "CN" ? "lang-cn" : "lang-en"}`}
      lang={displayLanguage === "CN" ? "zh-Hans" : "en"}
    >
      <ScrollToTop />
      <NavBar
        onMenuHiddenChange={setIsMenuHidden}
        language={displayLanguage}
        selectedLanguage={language}
        languageSwitchPhase={languageSwitchPhase}
        onLanguageChange={handleLanguageChange}
      />
      {!isHome && <DarkModeToggle isMenuHidden={isMenuHidden} />}
      <LanguageSwitcher language={language} onChange={handleLanguageChange} />
      <div
        className={`main-content${isHome ? " no-padding" : ""}${
          languageSwitchPhase
            ? ` lang-switching lang-${languageSwitchPhase}`
            : ""
        }`}
      >
        {isHome ? (
          <Routes>
            <Route path="/" element={<HomePage language={displayLanguage} />} />
          </Routes>
        ) : (
          <div className="container">
            <Routes>
              <Route
                path="/photographers"
                element={<PhotographersPage language={displayLanguage} />}
              />
              <Route
                path="/photographers/:id"
                element={<PhotographerDetail language={displayLanguage} />}
              />
              <Route
                path="/timeline"
                element={
                  <div className="timeline-page-container">
                    <TimelineIntro />
                    <TimelinePage
                      isMenuHidden={isMenuHidden}
                      language={displayLanguage}
                      targetEventId={
                        new URLSearchParams(location.search).get("event")
                      }
                    />
                  </div>
                }
              />
              <Route
                path="/genres"
                element={<GenrePage language={displayLanguage} />}
              />
              <Route
                path="/genres/:subgenreId"
                element={<SubgenreDetailPage language={displayLanguage} />}
              />
              <Route path="/about" element={<About language={displayLanguage} />} />
              {/* 可以添加更多路由 */}
            </Routes>
            <Footer language={displayLanguage} />
          </div>
        )}
      </div>
      <NewsletterPrompt language={displayLanguage} />
    </div>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
